import {
	AC_CARD_STATUSES,
	ACCREDITATION_PAGE_SIZE_DEFAULT,
	ACCREDITATION_TRAVEL_FILTERS,
	ACCREDITATION_PAGE_SIZES,
	ACCREDITATION_SORTS,
	type AcCardStatus,
	type AccreditationListItem,
	type AccreditationSort,
	BULK_ACCREDITATIONS_MAX_FILTER,
	BULK_ACCREDITATIONS_MAX_IDS,
} from "@seagames/contracts";
import { DataTable, KeysetPager, ListToolbar, Modal, ModalActions, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, createFileRoute, useParams } from "@tanstack/react-router";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ActivityModal } from "../../components/accreditation/activity";
import { CountsCard } from "../../components/accreditation/counts-card";
import { EMPTY_RECORD_FILTERS, FilterSheet, type RecordFilters, activeFilterCount } from "../../components/accreditation/filter-sheet";
import { DownloadIcon, GridIcon, LayersIcon, NoPhotoGlyph, PlusIcon, PrinterIcon, RowsIcon, SlidersIcon, ZapIcon } from "../../components/accreditation/icons";
import { actorSides, canRegister, hiddenActionsFor, readableOrganisations } from "../../components/accreditation/model";
import { RecordRow } from "../../components/accreditation/record-row";
import { RowMenu, useNationality, useRecordsColumns } from "../../components/accreditation/records-table";
import { RefusedList, StatusSheet, type StatusSheetTarget, refusalLabel } from "../../components/accreditation/status-sheet";
import { selectionCsv } from "../../components/accreditation/csv";
import { usePageBar } from "../../components/shell/page-bar";
import {
	type AccreditationFilter,
	type BulkResult,
	type BulkTarget,
	accreditationListOptions,
	accreditationSummaryOptions,
	bulkAccreditationUrgent,
	downloadAccreditationsCsv,
	invalidateAccreditations,
	saveBlob,
} from "../../lib/api/accreditations";
import { errorMessage } from "../../lib/api/messages";
import { gamesOptionsOptions } from "../../lib/competition/games";
import { keysetSearch, searchText } from "../../lib/list-page/list-search";
import { useListPage } from "../../lib/list-page/use-list-page";
import { responsibleOrganizationOptionsOptions } from "../../lib/responsible-organizations/queries";
import { toast } from "../../lib/ui/toast";

/**
 * URL = the list's state, in the API's own keys (`accreditationListQuery`):
 * filters, `q`, `sort`, `limit`, and the keyset `cursor` + `prev` stack.
 * `status` is the wire's comma list. Every param is lenient — a hand-edited
 * value falls back to "not set".
 */
export const Route = createFileRoute("/_app/accreditation/records")({
	validateSearch: keysetSearch({
		games_id: z.string().uuid(),
		status: z.string().max(200),
		responsible_organization_id: z.string().uuid(),
		main_function_id: z.string().uuid(),
		sub_agency_id: z.string().uuid(),
		sub_function_id: z.string().uuid(),
		urgent: z.boolean(),
		multicard: z.boolean(),
		no_photo: z.boolean(),
		travel: z.enum(ACCREDITATION_TRAVEL_FILTERS),
		q: searchText(),
		sort: z.enum(ACCREDITATION_SORTS),
	}),
	component: RecordsShell,
});

/** The record page ($id) nests under this route: when it matched, render it instead of the list. */
function RecordsShell() {
	const params = useParams({ strict: false });
	return params.id !== undefined ? <Outlet /> : <RecordsPage />;
}

type View = "rows" | "table";
const VIEW_KEY = "seagames.records.view";
function readView(): View {
	try {
		return window.localStorage.getItem(VIEW_KEY) === "table" ? "table" : "rows";
	} catch {
		return "rows";
	}
}

/** "Both names": the full name with the Accreditation Name under it. On unless this viewer turned it off. */
const NAMES_KEY = "seagames.records.bothNames";
function readBothNames(): boolean {
	try {
		return window.localStorage.getItem(NAMES_KEY) !== "off";
	} catch {
		return true;
	}
}

const parseStatuses = (s: string | undefined): AcCardStatus[] =>
	(s ?? "")
		.split(",")
		.map((x) => x.trim())
		.filter((x): x is AcCardStatus => (AC_CARD_STATUSES as readonly string[]).includes(x));

function RecordsPage() {
	const { t } = useTranslation("accreditation");
	const { t: tc } = useTranslation("common");
	const { t: tf } = useTranslation("files");
	const { t: tt } = useTranslation("travel");
	const { formatNumber: fmt } = useUiStrings();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const { session } = Route.useRouteContext();
	const role = session.active_role;
	// Every GMS role may filter by organisation (B7) — hidden only when the login reads ONE organisation,
	// and shown anyway while the URL carries one, so a URL filter is never invisible.
	const orgScope = useMemo(() => readableOrganisations(session), [session]);
	const showOrgFilter = orgScope.show || !!search.responsible_organization_id;
	const sides = useMemo(() => actorSides(session), [session]);
	const hiddenActions = useMemo(() => hiddenActionsFor(session), [session]);
	const nationality = useNationality();
	usePageBar({ title: t("records.pageTitle") });

	const [view, setViewState] = useState<View>(readView);
	const setView = (v: View) => {
		setViewState(v);
		try {
			window.localStorage.setItem(VIEW_KEY, v);
		} catch {
			/* per-viewer convenience only */
		}
	};

	const [bothNames, setBothNamesState] = useState<boolean>(readBothNames);
	const toggleBothNames = () => {
		const next = !bothNames;
		setBothNamesState(next);
		try {
			window.localStorage.setItem(NAMES_KEY, next ? "on" : "off");
		} catch {
			/* per-viewer convenience only */
		}
	};

	// ── Filters: URL → wire ────────────────────────────────────────────────
	const statuses = parseStatuses(search.status);
	const filter: AccreditationFilter = {
		games_id: search.games_id,
		status: statuses.length ? statuses.join(",") : undefined,
		responsible_organization_id: search.responsible_organization_id,
		main_function_id: search.main_function_id,
		sub_agency_id: search.sub_agency_id,
		sub_function_id: search.sub_function_id,
		urgent: search.urgent ? "true" : undefined,
		multicard: search.multicard ? "true" : undefined,
		no_photo: search.no_photo ? "true" : undefined,
		travel: search.travel,
		q: search.q,
	};
	const sort: AccreditationSort = search.sort ?? "number_desc";
	const applied: RecordFilters = {
		games_id: search.games_id ?? "",
		status: statuses,
		responsible_organization_id: search.responsible_organization_id ?? "",
		sub_agency_id: search.sub_agency_id ?? "",
		main_function_id: search.main_function_id ?? "",
		sub_function_id: search.sub_function_id ?? "",
		urgent: !!search.urgent,
		multicard: !!search.multicard,
		no_photo: !!search.no_photo,
	};
	const filterKey = JSON.stringify(filter);

	const summary = useQuery(accreditationSummaryOptions(filter));
	const page = useListPage({
		paging: "keyset",
		search,
		navigate,
		listOptions: (q) => accreditationListOptions({ ...filter, sort, ...q }),
		columnsKey: "accreditation-records",
		pageSizes: ACCREDITATION_PAGE_SIZES,
		defaultPageSize: ACCREDITATION_PAGE_SIZE_DEFAULT,
		totalFallback: summary.data?.total,
		summary: {
			showing: (v) => t("records.pager.showing", v),
			showingNoTotal: (v) => t("records.pager.showingNoTotal", v),
			empty: t("records.pager.empty"),
		},
	});
	const total = page.total;

	const writeFilters = (f: RecordFilters) =>
		page.patch({
			games_id: f.games_id || undefined,
			status: f.status.length ? f.status.join(",") : undefined,
			responsible_organization_id: f.responsible_organization_id || undefined,
			sub_agency_id: f.sub_agency_id || undefined,
			main_function_id: f.main_function_id || undefined,
			sub_function_id: f.sub_function_id || undefined,
			urgent: f.urgent || undefined,
			multicard: f.multicard || undefined,
			no_photo: f.no_photo || undefined,
		});
	const clearAll = () => page.patch({ ...Object.fromEntries(Object.keys(EMPTY_RECORD_FILTERS).map((k) => [k, undefined])), travel: undefined, q: undefined });

	// ── Selection: survives paging, resets when the result set changes ──────
	const [selected, setSelected] = useState<Map<string, AccreditationListItem>>(new Map());
	const [allMatching, setAllMatching] = useState(false);
	useEffect(() => {
		setSelected(new Map());
		setAllMatching(false);
	}, [filterKey]);
	const clearSelection = () => {
		setSelected(new Map());
		setAllMatching(false);
	};
	const toggleRow = (row: AccreditationListItem, on: boolean) => {
		setAllMatching(false);
		setSelected((prev) => {
			const next = new Map(prev);
			if (on) next.set(row.ac_card_id, row);
			else next.delete(row.ac_card_id);
			return next;
		});
	};
	const pageAllSelected = page.rows.length > 0 && page.rows.every((r) => selected.has(r.ac_card_id));
	const togglePage = () => {
		setAllMatching(false);
		setSelected((prev) => {
			const next = new Map(prev);
			for (const r of page.rows) {
				if (pageAllSelected) next.delete(r.ac_card_id);
				else next.set(r.ac_card_id, r);
			}
			return next;
		});
	};
	const selectionCount = allMatching ? (total ?? 0) : selected.size;

	// ── Sheets ─────────────────────────────────────────────────────────────
	const [filtersOpen, setFiltersOpen] = useState(false);
	const [statusTarget, setStatusTarget] = useState<StatusSheetTarget | null>(null);
	const [urgentAsk, setUrgentAsk] = useState<boolean | null>(null);
	const [urgentBusy, setUrgentBusy] = useState(false);
	// The rows the last bulk urgent left alone, with names while the page still knows them (B5).
	const [urgentRefused, setUrgentRefused] = useState<{ refused: BulkResult["refused"]; rows: ReturnType<typeof sheetRow>[] } | null>(null);
	const [whatChanged, setWhatChanged] = useState<AccreditationListItem | null>(null);
	const [exporting, setExporting] = useState(false);

	const sheetRow = (r: AccreditationListItem) => ({
		ac_card_id: r.ac_card_id,
		status: r.status,
		printed_name: r.person.printed_name,
		registration_no_display: r.registration_no_display,
	});
	const openBulkStatus = () =>
		setStatusTarget(
			allMatching
				? { kind: "filter", filter, expectedCount: total ?? 0, byStatus: summary.data?.by_status ?? [] }
				: { kind: "ids", rows: [...selected.values()].map(sheetRow) },
		);

	const bulkTarget = (): BulkTarget => (allMatching ? { filter, expected_count: total ?? 0 } : { ids: [...selected.keys()] });
	const runUrgent = async (urgent: boolean) => {
		setUrgentBusy(true);
		try {
			const named = [...selected.values()].map(sheetRow);
			const res = await bulkAccreditationUrgent(urgent, bulkTarget());
			await invalidateAccreditations();
			if (res.updated > 0)
				toast.success(
					urgent ? t("records.urgent.doneOn", { count: res.updated, n: fmt(res.updated) }) : t("records.urgent.doneOff", { count: res.updated, n: fmt(res.updated) }),
				);
			if (res.refused.length > 0) {
				// The dialog stays, listing who was left alone and why.
				setUrgentRefused({ refused: res.refused, rows: named });
			} else {
				setUrgentAsk(null);
			}
			clearSelection();
		} catch (err) {
			toast.error(err, t("records.urgent.failed"));
		} finally {
			setUrgentBusy(false);
		}
	};
	const toggleRowUrgent = async (row: AccreditationListItem) => {
		try {
			const res = await bulkAccreditationUrgent(!row.urgent_printing, { ids: [row.ac_card_id] });
			await invalidateAccreditations();
			const refusal = res.refused[0];
			if (res.updated === 0 && refusal) toast.error(new Error(t("record.urgentRefused", { reason: refusalLabel(t, refusal.reason) })), t("records.urgent.failed"));
			else toast.success(row.urgent_printing ? t("record.urgentUndone") : t("record.urgentDone"));
		} catch (err) {
			toast.error(err, t("records.urgent.failed"));
		}
	};

	const today = new Date().toISOString().slice(0, 10);
	const exportFilename = `${t("records.export.filename")}-${today}.csv`;
	const exportCsv = async () => {
		setExporting(true);
		try {
			if (!allMatching && selected.size > 0) {
				saveBlob(selectionCsv([...selected.values()], t, nationality), exportFilename);
			} else {
				await downloadAccreditationsCsv(filter, exportFilename);
			}
			toast.success(t("records.export.started"));
		} catch (err) {
			toast.error(err, t("records.export.failed"));
		} finally {
			setExporting(false);
		}
	};

	// ── Chips ──────────────────────────────────────────────────────────────
	const games = useQuery(gamesOptionsOptions());
	const orgs = useQuery({ ...responsibleOrganizationOptionsOptions(), enabled: showOrgFilter });
	const orgOptions = (orgs.data ?? []).filter((o) => !orgScope.ids || orgScope.ids.includes(o.responsible_organization_id) || o.responsible_organization_id === search.responsible_organization_id);
	const statusCounts = new Map((summary.data?.by_status ?? []).map((b) => [b.status, b.count]));
	const filterCount = activeFilterCount(applied);
	const columns = useRecordsColumns(bothNames);

	const namesSwitch = (
		<button
			type="button"
			role="switch"
			aria-checked={bothNames}
			onClick={toggleBothNames}
			title={t("records.names.toggleHint")}
			className="flex cursor-pointer items-center gap-[0.5rem] rounded-[0.25rem] text-[0.8125rem] font-medium whitespace-nowrap text-gms-ink-2 focus-visible:ring-2 focus-visible:ring-gms-navy/40 focus-visible:outline-none"
		>
			<span aria-hidden className={`relative h-[1rem] w-[1.75rem] shrink-0 rounded-full transition-colors ${bothNames ? "bg-gms-navy" : "bg-gms-line-2"}`}>
				<span className={`absolute top-[0.125rem] size-[0.75rem] rounded-full bg-white transition-[left] ${bothNames ? "left-[0.875rem]" : "left-[0.125rem]"}`} />
			</span>
			{t("records.names.toggle")}
		</button>
	);

	const pager = (position: "top" | "bottom") =>
		page.list.isError || (position === "bottom" && page.rows.length === 0) ? null : (
			<KeysetPager
				{...page.pager}
				position={position}
				className={pagerLook}
				start={
					position === "top" && page.rows.length > 0 ? (
						<span className="flex flex-wrap items-center gap-x-[1rem] gap-y-[0.5rem]">
							{view === "rows" && (
								<label className="flex cursor-pointer items-center gap-[0.5rem] text-[0.8125rem] font-medium text-gms-ink-2" title={t("records.pager.selectPage")}>
									<input
										type="checkbox"
										className="checkbox size-[1.125rem] rounded-[0.25rem] border-gms-line-2 checked:border-gms-navy checked:bg-gms-navy checked:text-white"
										checked={pageAllSelected}
										onChange={togglePage}
										aria-label={t("records.pager.selectPage")}
									/>
									<span aria-hidden>{tc("select.selectAll")}</span>
								</label>
							)}
							{namesSwitch}
						</span>
					) : undefined
				}
			/>
		);

	const viewBtn = (v: View, label: string, icon: ReactNode) => (
		<button
			type="button"
			aria-pressed={view === v}
			aria-label={label}
			title={label}
			onClick={() => setView(v)}
			className={`grid size-[2.5rem] cursor-pointer place-items-center rounded-[0.375rem] transition-colors [&>svg]:size-[1.125rem] ${
				view === v ? "bg-base-100 text-gms-navy shadow-[0_0_0_1px_var(--color-gms-line)]" : "text-gms-ink-3 hover:text-gms-navy"
			}`}
		>
			{icon}
		</button>
	);

	const hasAnyFilter = filterCount > 0 || !!search.q || !!search.travel;

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			{/* Her `.pgh.rh`: the count bottom-left, the actions right; on phones the red CTA first, the others icon-only. */}
			<div className="mb-[0.125rem] flex flex-wrap items-end justify-between gap-[0.875rem]">
				<p className="text-[0.875rem] leading-normal text-gms-ink-2 tabular-nums">{total === null ? "\u00a0" : <RecordCount total={total} />}</p>
				<div className="flex flex-wrap gap-[0.5rem] max-[45rem]:w-full">
					<button
						type="button"
						onClick={() => void exportCsv()}
						disabled={exporting}
						title={t("records.exportCsv")}
						aria-label={t("records.exportCsv")}
						className={`${headBtn} btn-line text-gms-navy-ink [&>svg]:text-gms-ink-2 max-[45rem]:w-[2.875rem] max-[45rem]:flex-[0_0_2.875rem] max-[45rem]:px-0`}
					>
						<DownloadIcon />
						<span className="max-[45rem]:hidden">{t("records.exportCsv")}</span>
					</button>
					{canRegister(session) && (
						<Link
							to="/accreditation/add"
							className={`${headBtn} btn-accent min-w-0 border-gms-live bg-gms-live hover:border-[#c8161d] hover:bg-[#c8161d] max-[45rem]:order-first max-[45rem]:flex-1 max-[45rem]:px-[0.625rem]`}
						>
							<PlusIcon />
							<span className="truncate">{t("records.register")}</span>
						</Link>
					)}
				</div>
			</div>

			<CountsCard
				showOrganisations={showOrgFilter}
				onPickFunction={(id) => page.patch({ main_function_id: id, sub_function_id: undefined })}
				onPickOrganisation={(id) => page.patch({ responsible_organization_id: id, sub_agency_id: undefined, main_function_id: undefined, sub_function_id: undefined })}
			/>

			<ListToolbar
				search={search.q ?? ""}
				onSearch={(v) => page.patch({ q: v || undefined })}
				searchPlaceholder={t("records.searchPlaceholder")}
				afterSearch={
					<button
						type="button"
						onClick={() => setFiltersOpen(true)}
						aria-label={filterCount > 0 ? t("records.filtersCount", { n: fmt(filterCount) }) : t("records.filters")}
						className={`inline-flex h-[2.875rem] shrink-0 cursor-pointer items-center justify-center gap-[0.5rem] rounded-[0.5rem] border px-[0.875rem] text-[0.875rem] font-semibold transition-colors [&>svg]:size-[1.125rem] max-[45rem]:relative max-[45rem]:w-[2.875rem] max-[45rem]:px-0 ${
							filterCount > 0 ? "border-gms-navy bg-gms-navy-soft text-gms-navy" : "border-gms-line-2 bg-base-100 text-gms-navy-ink hover:border-gms-navy [&>svg]:text-gms-ink-2"
						}`}
					>
						<SlidersIcon />
						{/* Her phone `.rtools__f`: the glyph alone, the count as a badge. */}
						<span className="max-[45rem]:hidden">{filterCount > 0 ? t("records.filtersCount", { n: fmt(filterCount) }) : t("records.filters")}</span>
						{filterCount > 0 && (
							<span
								aria-hidden
								className="absolute -top-[0.375rem] -right-[0.375rem] grid h-[1.125rem] min-w-[1.125rem] place-items-center rounded-full bg-gms-navy px-[0.25rem] text-[0.6875rem] font-bold text-white min-[45rem]:hidden"
							>
								{fmt(filterCount)}
							</span>
						)}
					</button>
				}
				sort={{
					value: sort,
					options: ACCREDITATION_SORTS.map((s) => ({ value: s, label: t(`records.sort.${s}`) })),
					onChange: (v) => page.patch({ sort: v === "number_desc" ? undefined : (v as AccreditationSort) }),
				}}
				end={
					<div role="group" aria-label={t("records.view.label")} className="flex shrink-0 gap-[0.125rem] rounded-[0.5rem] bg-gms-soft-2 p-[0.1875rem]">
						{viewBtn("rows", t("records.view.rows"), <GridIcon />)}
						{viewBtn("table", t("records.view.table"), <RowsIcon />)}
					</div>
				}
				chips={[
					{
						kind: "multi-menu",
						key: "status",
						label: t("records.chips.status"),
						options: AC_CARD_STATUSES.map((s) => ({
							value: s,
							label: t(`record.status.${s}`),
							count: statuses.length === 0 ? statusCounts.get(s) : undefined,
						})),
						value: statuses,
						onChange: (v) => page.patch({ status: v.length ? v.join(",") : undefined }),
					},
					...(showOrgFilter
						? [
								{
									kind: "menu" as const,
									key: "organisation",
									label: t("records.chips.organisation"),
									searchPlaceholder: t("records.chips.organisationSearch"),
									options: orgOptions.map((o) => ({ value: o.responsible_organization_id, label: o.name })),
									value: search.responsible_organization_id ?? null,
									onChange: (v: string | null) =>
										page.patch({ responsible_organization_id: v ?? undefined, sub_agency_id: undefined, main_function_id: undefined, sub_function_id: undefined }),
								},
							]
						: []),
					...((games.data?.length ?? 0) > 1
						? [
								{
									kind: "menu" as const,
									key: "games",
									label: t("records.chips.games"),
									options: (games.data ?? []).map((g) => ({ value: g.games_id, label: g.name })),
									value: search.games_id ?? null,
									onChange: (v: string | null) => page.patch({ games_id: v ?? undefined }),
								},
							]
						: []),
					{
						kind: "toggle",
						key: "urgent",
						icon: <PrinterIcon className="size-[1rem]" />,
						label: t("records.chips.urgent"),
						pressed: !!search.urgent,
						onChange: (on) => page.patch({ urgent: on || undefined }),
					},
					{
						kind: "toggle",
						key: "multicard",
						icon: <LayersIcon className="size-[1rem]" />,
						label: t("records.chips.multicard"),
						pressed: !!search.multicard,
						onChange: (on) => page.patch({ multicard: on || undefined }),
					},
					{
						kind: "toggle",
						key: "no_photo",
						icon: <NoPhotoGlyph className="size-[1rem]" />,
						label: summary.data?.no_photo ? tf("chip.noPhotoCount", { count: summary.data.no_photo, n: fmt(summary.data.no_photo) }) : t("records.chips.noPhoto"),
						pressed: !!search.no_photo,
						onChange: (on) => page.patch({ no_photo: on || undefined }),
					},
					// M9: shown only while the URL carries it (the A&D tiles link here) — a filter is never invisible.
					...(search.travel
						? [
								{
									kind: "toggle" as const,
									key: "travel",
									label: tt(`recordsFilter.${search.travel}`),
									pressed: true,
									onChange: (on: boolean) => page.patch({ travel: on ? search.travel : undefined }),
								},
							]
						: []),
				]}
				active={hasAnyFilter}
				onClearAll={clearAll}
			/>

			{selectionCount > 0 && (
				<div
					role="region"
					aria-label={t("records.bulk.selected", { count: selectionCount, n: fmt(selectionCount) })}
					className="flex flex-wrap items-center gap-x-[0.75rem] gap-y-[0.5rem] rounded-[0.625rem] bg-gms-navy px-[0.875rem] py-[0.5rem] text-white"
				>
					<span className="text-[0.84375rem] font-semibold" aria-live="polite">
						{allMatching ? t("records.bulk.allSelected", { count: selectionCount, n: fmt(selectionCount) }) : t("records.bulk.selected", { count: selectionCount, n: fmt(selectionCount) })}
					</span>
					{!allMatching && total !== null && total > selected.size && total <= BULK_ACCREDITATIONS_MAX_FILTER && (
						<button type="button" onClick={() => setAllMatching(true)} className="text-[0.84375rem] font-semibold text-gms-nav-yellow hover:underline">
							{t("records.bulk.selectAll", { count: total, n: fmt(total) })}
						</button>
					)}
					<div className="ml-auto flex flex-wrap items-center gap-[0.375rem]">
						<BulkButton onClick={openBulkStatus} disabled={!allMatching && selected.size > BULK_ACCREDITATIONS_MAX_IDS} title={!allMatching && selected.size > BULK_ACCREDITATIONS_MAX_IDS ? t("records.bulk.tooMany", { max: fmt(BULK_ACCREDITATIONS_MAX_IDS) }) : undefined}>
							{t("records.bulk.changeStatus")}
						</BulkButton>
						<BulkButton onClick={() => setUrgentAsk(true)} icon={<ZapIcon />}>
							{t("records.bulk.markUrgent")}
						</BulkButton>
						<BulkButton onClick={() => setUrgentAsk(false)}>{t("records.bulk.removeUrgent")}</BulkButton>
						<BulkButton onClick={() => void exportCsv()} icon={<DownloadIcon />} disabled={exporting}>
							{t("records.bulk.exportCsv")}
						</BulkButton>
						<button
							type="button"
							onClick={clearSelection}
							aria-label={t("records.bulk.clear")}
							title={t("records.bulk.clear")}
							className="grid size-[2rem] place-items-center rounded-[0.375rem] border border-white/25 text-white/85 hover:bg-white/10"
						>
							<svg aria-hidden viewBox="0 0 24 24" fill="none" className="size-[0.875rem]">
								<path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
							</svg>
						</button>
					</div>
				</div>
			)}

			{view === "table" ? (
				<DataTable<AccreditationListItem, string>
					{...page.table}
					variant="list"
					columns={columns}
					rowKey={(r) => r.ac_card_id}
					pager={pager}
					selection={{
						selected: new Set(selected.keys()),
						onChange: (next) => {
							setAllMatching(false);
							setSelected((prev) => {
								const out = new Map<string, AccreditationListItem>();
								for (const id of next) {
									const row = prev.get(id) ?? page.rows.find((r) => r.ac_card_id === id);
									if (row) out.set(id, row);
								}
								return out;
							});
						},
					}}
					rowActions={(r) => <RowMenu row={r} role={role} onChangeStatus={(row) => setStatusTarget({ kind: "single", row: sheetRow(row) })} onToggleUrgent={(row) => void toggleRowUrgent(row)} />}
					empty={
						hasAnyFilter
							? {
									title: t("records.empty.filteredTitle"),
									description: t("records.empty.filteredDescription"),
									action: (
										<button type="button" className="btn btn-sm btn-outline" onClick={clearAll}>
											{t("records.empty.clearFilters")}
										</button>
									),
								}
							: { title: t("records.empty.noneTitle"), description: t("records.empty.noneDescription") }
					}
				/>
			) : (
				<div className="flex min-w-0 flex-col gap-[0.875rem]">
					{pager("top")}
					{page.list.isError ? (
						<div role="alert" className="grid justify-items-center gap-[0.5rem] rounded-[0.625rem] border border-gms-line bg-base-100 px-[1rem] py-[2.5rem] text-center">
							<b className="text-[0.9375rem] text-gms-navy-ink">{tc("dataTable.errorTitle")}</b>
							<p className="text-[0.8125rem] text-gms-ink-3">{errorMessage(page.list.error)}</p>
							<button type="button" className="btn btn-sm btn-outline mt-[0.25rem]" onClick={() => void page.list.refetch()}>
								{tc("dataTable.tryAgain")}
							</button>
						</div>
					) : page.list.isPending ? (
						<div className="grid gap-[1rem]" aria-busy>
							{[0, 1, 2, 3].map((i) => (
								<div key={i} className="skeleton h-[9rem] rounded-[0.625rem]" />
							))}
						</div>
					) : page.rows.length === 0 ? (
						<div className="grid justify-items-center gap-[0.5rem] rounded-[0.625rem] border border-gms-line bg-base-100 px-[1rem] py-[2.5rem] text-center">
							<b className="text-[0.9375rem] text-gms-navy-ink">{hasAnyFilter ? t("records.empty.filteredTitle") : t("records.empty.noneTitle")}</b>
							<p className="text-[0.8125rem] text-gms-ink-3">{hasAnyFilter ? t("records.empty.filteredDescription") : t("records.empty.noneDescription")}</p>
							{hasAnyFilter && (
								<button type="button" className="btn btn-sm btn-outline mt-[0.25rem]" onClick={clearAll}>
									{t("records.empty.clearFilters")}
								</button>
							)}
						</div>
					) : (
						<div className={`grid gap-[1rem] transition-opacity ${page.list.isFetching ? "opacity-60" : ""}`}>
							{page.rows.map((r) => (
								<RecordRow
									key={r.ac_card_id}
									row={r}
									role={role}
									selected={allMatching || selected.has(r.ac_card_id)}
									onSelect={(on) => toggleRow(r, on)}
									onToggleUrgent={(row) => void toggleRowUrgent(row)}
									onWhatChanged={setWhatChanged}
									bothNames={bothNames}
								/>
							))}
						</div>
					)}
					{pager("bottom")}
				</div>
			)}

			<FilterSheet open={filtersOpen} onClose={() => setFiltersOpen(false)} applied={applied} onApply={writeFilters} showOrganisation={showOrgFilter} organisationIds={orgScope.ids} />

			<StatusSheet
				hiddenActions={hiddenActions}
				open={statusTarget !== null}
				target={statusTarget}
				onClose={() => setStatusTarget(null)}
				sides={sides}
				onDeselect={(ids) =>
					setSelected((prev) => {
						const next = new Map(prev);
						for (const id of ids) next.delete(id);
						return next;
					})
				}
				onDone={() => {
					if (statusTarget?.kind !== "single") clearSelection();
				}}
			/>

			<Modal
				open={urgentAsk !== null}
				onClose={() => {
					if (urgentBusy) return;
					setUrgentAsk(null);
					setUrgentRefused(null);
				}}
				dismissible={!urgentBusy}
				size="sm"
				title={urgentAsk ? t("records.urgent.onTitle") : t("records.urgent.offTitle")}
				footer={
					<ModalActions>
						{urgentRefused ? (
							<button
								type="button"
								className="btn btn-sm border-gms-navy bg-gms-navy text-white hover:bg-gms-navy-lift"
								onClick={() => {
									setUrgentAsk(null);
									setUrgentRefused(null);
								}}
							>
								{t("records.statusSheet.close")}
							</button>
						) : (
							<>
								<button type="button" className="btn btn-ghost btn-sm" onClick={() => setUrgentAsk(null)} disabled={urgentBusy}>
									{t("records.statusSheet.cancel")}
								</button>
								<button
									type="button"
									className="btn btn-sm border-gms-navy bg-gms-navy text-white hover:bg-gms-navy-lift"
									disabled={urgentBusy}
									onClick={() => void runUrgent(!!urgentAsk)}
								>
									{urgentBusy && <span className="loading loading-spinner loading-xs" />}
									{urgentAsk ? t("records.urgent.confirmOn") : t("records.urgent.confirmOff")}
								</button>
							</>
						)}
					</ModalActions>
				}
			>
				{urgentRefused ? (
					<RefusedList refused={urgentRefused.refused} rows={urgentRefused.rows} />
				) : (
					<p className="text-[0.875rem] text-gms-ink-2">
						{urgentAsk
							? t("records.urgent.onBody", { count: selectionCount, n: fmt(selectionCount) })
							: t("records.urgent.offBody", { count: selectionCount, n: fmt(selectionCount) })}
					</p>
				)}
			</Modal>

			<ActivityModal row={whatChanged} onClose={() => setWhatChanged(null)} canInspect={role === "SUPER_ADMIN"} />
		</div>
	);
}

/**
 * Her `.pgr` / `.pgn` over the shared KeysetPager (which stays keyset — no
 * page numbers a cursor cannot jump to): the size select 113px with room for
 * its arrow, and prev / next as her borderless 34px `.pgn__b`.
 */
const pagerLook =
	"[&>select]:h-[2.25rem] [&>select]:pr-[0.5rem] [&>select]:pl-[0.75rem] [&>div]:gap-[0.125rem] [&>div>button]:border-transparent [&>div>button]:bg-transparent [&>div>button:hover:enabled]:bg-gms-navy-soft [&>div>button:hover:enabled]:text-gms-navy";

/** Her `.rh__a .btn`: 42px, 14px semibold, 14px sides, 18px glyph. */
const headBtn = "btn h-[2.625rem] min-h-[2.625rem] gap-[0.5rem] rounded-[0.5rem] px-[0.875rem] text-[0.875rem] font-semibold shadow-none [&>svg]:size-[1.125rem] [&>svg]:shrink-0";

/** "**327** records" — the number bold in whatever order the locale puts it. */
function RecordCount({ total }: { total: number }) {
	const { t } = useTranslation("accreditation");
	const { formatNumber: fmt } = useUiStrings();
	const MARK = "\u0001";
	const [before = "", after = ""] = t("records.count", { count: total, n: MARK }).split(MARK);
	return (
		<>
			{before}
			<b className="font-semibold text-gms-navy-ink">{fmt(total)}</b>
			{after}
		</>
	);
}

function BulkButton({ children, onClick, icon, disabled, title }: { children: ReactNode; onClick: () => void; icon?: ReactNode; disabled?: boolean; title?: string }) {
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			title={title}
			className="inline-flex h-[2rem] items-center gap-[0.375rem] rounded-[0.375rem] bg-white/12 px-[0.75rem] text-[0.8125rem] font-semibold text-white transition-colors hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-40 [&>svg]:size-[0.9375rem]"
		>
			{icon}
			{children}
		</button>
	);
}
